import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DailyCallVolumeReportParameters } from '@ndt/report-output/daily-call-volume/daily-call-volume';
import { BarGraphSourceReportParameter } from '@ndt/report-output/shared/models/bar-graph-source-report-parameter';
import { ReportCallDirections } from '@ndt/report-output/shared/models/call-direction-report-parameter';
import { defaultAllMultiEntitySelectParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { defaultBarGraphSourceParameter } from '@ndt/reports/shared/models/bar-graph-source-report-parameter-value';
import { createDateRangeReportParameter } from '@ndt/reports/shared/models/date-range-report-parameter-value';
import { defaultDaysOfWeekParameter } from '@ndt/reports/shared/models/day-of-week-report-parameter-value';
import { defaultTimeZoneParameter } from '@ndt/reports/shared/models/time-zone-parameter-value';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   CallDirectionsToIncludeReportParameter,
   convertToDateRangeReportParameter,
   convertToForm,
   DaysOfWeekReportParameter,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '@ndt/reports/shared/report-parameter-list/services/report-parameter-form.service';
import { ReportParameters } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';

export class DailyCallVolumeParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<DailyCallVolumeReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(fb);
      const initialValue: ReportParametersUnion[] = [
         {
            accountId: 0,
            all: true,
            names: [],
            parameterType: ReportParameters.ExtensionsToInclude,
            sortOrder: 1,
            values: [],
         },
         {
            accountId: 0,
            all: false,
            parameterType: ReportParameters.ExtensionGroupsToInclude,
            sortOrder: 2,
            values: [],
            names: [],
         },
         {
            accountId: 0,
            all: true,
            names: [],
            parameterType: ReportParameters.PhoneNumbersToInclude,
            sortOrder: 4,
            values: [],
         },
         {
            parameterType: ReportParameters.DateRange,
            ...createDateRangeReportParameter(),
            sortOrder: 2,
         },
         {
            parameterType: ReportParameters.CallDirectionsToInclude,
            sortOrder: 3,
            value: ReportCallDirections.Both,
         },
         {
            parameterType: ReportParameters.DaysOfTheWeek,
            sortOrder: 5,
            values: [...defaultDaysOfWeekParameter.values],
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 6,
            value: '',
         },
         {
            parameterType: ReportParameters.BarGraphSource,
            sortOrder: 8,
            value: 'Count',
         },
      ];

      this.config = initialValue.map((ap) => {
         return this.getReportParameterConfig(ap.parameterType);
      });

      this.form = convertToForm(initialValue, fb);
   }
   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values, { emitEvent: false });
   }
   getValue(includeAskParameters?: boolean | undefined): DailyCallVolumeReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = formValue.values;
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }
   convertValue(values: ReportParametersUnion[]): DailyCallVolumeReportParameters | null {
      const extensions = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const extensionGroups = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionGroupsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const phoneNumbers = values.find((v) => {
         return v.parameterType === ReportParameters.PhoneNumbersToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const callDirectionsToInclude = values.find((v) => {
         return v.parameterType === ReportParameters.CallDirectionsToInclude;
      }) as CallDirectionsToIncludeReportParameter | undefined;

      const daysOfWeek = values.find((v) => {
         return v.parameterType === ReportParameters.DaysOfTheWeek;
      }) as DaysOfWeekReportParameter | undefined;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const barGraphSource = values.find((v) => {
         return v.parameterType === ReportParameters.BarGraphSource;
      }) as BarGraphSourceReportParameter | undefined;

      const output: DailyCallVolumeReportParameters = {
         dateRange: throwIfNullOrUndefined(convertToDateRangeReportParameter(values, this.dateAdapter)),
         callDirection: callDirectionsToInclude
            ? {
                 value: callDirectionsToInclude.value,
              }
            : {
                 value: ReportCallDirections.Both,
              },
         daysOfWeek: daysOfWeek
            ? {
                 values: daysOfWeek.values,
              }
            : defaultDaysOfWeekParameter[0],
         extensions: extensions
            ? {
                 accountId: extensions.accountId,
                 all: !!extensions?.all,
                 values: extensions.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         extensionGroups: extensionGroups
            ? {
                 accountId: extensionGroups.accountId,
                 all: !!extensionGroups?.all,
                 values: extensionGroups.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         phoneNumbers: phoneNumbers
            ? {
                 accountId: phoneNumbers.accountId,
                 all: !!phoneNumbers?.all,
                 values: phoneNumbers.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         timeZone: timeZone
            ? {
                 value: timeZone.value,
              }
            : defaultTimeZoneParameter,
         barGraphSource: barGraphSource
            ? {
                 value: barGraphSource.value,
              }
            : defaultBarGraphSourceParameter,
      };

      return output;
   }
   isListComplete(values: ReportParametersUnion[], includeAskParameters?: boolean | undefined): boolean {
      const validParameters = !includeAskParameters
         ? values.filter((v) => {
              return !false;
           })
         : values;

      const dateRange = validParameters.find((v) => {
         return v.parameterType === ReportParameters.DateRange;
      });

      return !!dateRange;
   }
   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      throw new Error('Method not implemented.');
   }
   resetConfig(): void {
      throw new Error('Method not implemented.');
   }
   // getLayoutConfigurations(values: ReportParametersUnion[]): {
   //    name: string | null;
   //    color: string | null;
   //    refreshTimeSeconds: number | null;
   // } {
   //    throw new Error('Method not implemented.');
   // }
}
